import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

// Admin-only workspace. Access is gated on two layers:
//   1. The hardcoded admin email on the client side (this file) — just a UX
//      fast-fail so a non-admin never sees the page chrome.
//   2. The backend `/api/admin/*` endpoints, which check the server-side
//      `is_admin` flag on the user profile. Never rely on (1) alone.
const ADMIN_EMAIL = 'silas.phiri21@gmail.com';

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState('developers');
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await api.get(
          tab === 'developers' ? '/api/admin/developers' : '/api/admin/users',
        );
        setRows(res.users || res.developers || []);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load');
      }
    })();
  }, [user, tab]);

  async function toggleDeveloper(uid, current) {
    try {
      await api.post(`/api/admin/users/${uid}/developer`, { enabled: !current });
      // Optimistic refresh
      setRows((prev) => prev.map((r) =>
        r.uid === uid ? { ...r, is_developer: !current } : r,
      ));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to toggle');
    }
  }

  async function grantCredits(uid, amount) {
    const n = Number(amount);
    if (!Number.isFinite(n) || n === 0) return;
    try {
      await api.post(`/api/admin/users/${uid}/credits`, { amount: n });
      setRows((prev) => prev.map((r) =>
        r.uid === uid ? { ...r, credits: (r.credits || 0) + n } : r,
      ));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to grant credits');
    }
  }

  if (loading) return <div className="center" style={{ padding: '4rem 0' }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if ((user.email || '').toLowerCase() !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="main-container">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div>
              <h1 className="mb-1" style={{ fontWeight: 700 }}>Admin</h1>
              <p className="text-muted mb-0">
                Signed in as <strong>{user.email}</strong>
              </p>
            </div>
            <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
              <i className="fas fa-arrow-left me-1"></i>Back to user view
            </Link>
          </div>

          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${tab === 'developers' ? 'active' : ''}`}
                onClick={() => setTab('developers')}
              >
                <i className="fas fa-code me-1"></i>Developers
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${tab === 'users' ? 'active' : ''}`}
                onClick={() => setTab('users')}
              >
                <i className="fas fa-users me-1"></i>All users
              </button>
            </li>
          </ul>

          {err && <div className="alert alert-danger">{err}</div>}

          <div className="card">
            <div className="card-body">
              {rows.length === 0 ? (
                <div className="text-muted small">No records yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Credits</th>
                        <th>Developer</th>
                        <th>Joined</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.uid}>
                          <td>{r.email}</td>
                          <td>{r.full_name || '—'}</td>
                          <td>{(r.credits || 0).toLocaleString()}</td>
                          <td>
                            {r.is_developer ? (
                              <span className="badge bg-success">Yes</span>
                            ) : (
                              <span className="badge bg-secondary">No</span>
                            )}
                          </td>
                          <td className="small text-muted">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => toggleDeveloper(r.uid, r.is_developer)}
                            >
                              {r.is_developer ? 'Disable dev' : 'Enable dev'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              onClick={() => {
                                const v = window.prompt('Grant credits (can be negative to deduct):', '100');
                                if (v != null) grantCredits(r.uid, v);
                              }}
                            >
                              +/− credits
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
